import { useState } from "react";
import {
  Accordion,
  Badge,
  Box,
  Card,
  Flex,
  HStack,
  Heading,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@/lib/convex";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { ListSkeleton } from "../components/LoadingSkeletons";
import { PageHeader } from "../components/PageHeader";
import { ProgressRows, type RowHighlight } from "../components/ProgressRows";
import { useEffectivePersonId } from "../impersonation";
import { PROGRAMME_LABELS } from "@convex/schemaEnums";
import { CoSupervisorCard } from "./CoSupervisorCard";
import type { ActivityOrigin } from "./activityOrigin";

function formatDay(day: string | null) {
  if (day === null) {
    return "—";
  }
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" })
    .format(new Date(`${day}T12:00:00Z`));
}

/**
 * One person's way through their programme, year by year.
 *
 * A record rather than a worklist: My Activities answers "what do I do next",
 * grouped by status; this answers "how am I doing", organised by time. Years
 * are derived from the person's own enrollment, so a cohort under the old
 * rules and one under the new need no configuration between them.
 *
 * Hidden entirely for anybody without an enrollment rather than shown empty —
 * most of the institute has no programme, and an empty skeleton would be
 * worse than no entry at all.
 */
export function ProgrammeProgressPage({ personId, from }: { personId?: string; from?: "group" | "coSupervision" }) {
  // Which year's count is being pointed at. Keyed by year for the same
  // reason the student view keys by person: two open years must not light
  // each other's rows.
  const [hovered, setHovered] = useState<{ year: number; what: RowHighlight } | null>(null);
  const effectivePersonId = useEffectivePersonId();
  const progress = useQuery(api.programmeProgress.forViewer, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
    personId: (personId ?? null) as Id<"persons"> | null,
  });

  // Somebody else's record reached from the matrix or a supervisor's list,
  // so the trail has to lead back to where they came from rather than to
  // a page about themselves.
  const breadcrumbs = personId === undefined
    ? [{ label: "CeMMbase", to: "/" }, { label: "My programme" }]
    : [
      { label: "CeMMbase", to: "/" },
      // The matrix's own trail, in full: it sits under Activities.
      ...(from === "group"
        ? [{ label: "My group", to: "/my-group" }]
        : from === "coSupervision"
          ? [{ label: "Co-supervision", to: "/co-supervision" }]
          : [{ label: "Activities", to: "/activities" }, { label: "Programme progress", to: "/activities/admin/progress" }]),
      { label: progress?.person ?? "Programme" },
    ];
  // An activity opened from here leads back here, the same way.
  const origin: ActivityOrigin = personId === undefined
    ? { from: "myProgramme" }
    : { from: "programme", via: from };

  if (progress === undefined) {
    return (
      <Box minH="full" bg="bg.subtle">
        <PageHeader
          title="My programme"
          description="Your way through the programme, year by year."
          breadcrumbs={breadcrumbs}
          // Somebody else's programme is titled with their name once it arrives.
          loading={personId !== undefined}
          meta={<Skeleton height="5" width={{ base: "64", sm: "sm" }} />}
        />
        <Box px={{ base: 6, sm: 8, lg: 10 }} py={8}>
          <ListSkeleton label="Loading the programme" rows={4} />
        </Box>
      </Box>
    );
  }

  if (progress === null) {
    return (
      <Box minH="full" bg="bg.subtle">
        <PageHeader title="My programme" description="Your way through the programme, year by year." breadcrumbs={breadcrumbs} />
        <Box px={{ base: 6, sm: 8, lg: 10 }} py={8}>
          <Text fontSize="sm" color="fg.muted">
            {personId === undefined
              ? "You are not enrolled in a programme, so there is nothing to show here."
              : "This person is not enrolled in a programme."}
          </Text>
        </Box>
      </Box>
    );
  }

  // Completed years collapse; the current one is open. Anything past the
  // current year opens too, since it is what is coming.
  const openYears = progress.years
    .filter((year) => year.year >= progress.currentYear)
    .map((year) => String(year.year));

  const totals = progress.years.flatMap((year) => year.rows).concat(progress.undated);
  const done = totals.filter((row) => row.status === "completed").length;

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={progress.person ?? "My programme"}
        description="Your way through the programme, year by year."
        breadcrumbs={breadcrumbs}
        // What this enrollment is, in one line under the name, as an
        // activity's facts are. It used to stand alone above the years.
        meta={
          <HStack gap={4} flexWrap="wrap" fontSize="sm" color="fg.muted">
            <Text fontWeight="semibold">
              {PROGRAMME_LABELS[progress.programme]}
              {progress.cohort === null ? "" : ` · ${progress.cohort} cohort`}
            </Text>
            <Text>Started {formatDay(progress.startDate)}</Text>
            {progress.pausedDays > 0 ? (
              <Text color="purple.600">
                {progress.pausedDays} day{progress.pausedDays === 1 ? "" : "s"} paused — dates and years both moved
              </Text>
            ) : null}
            {progress.defenceDate ? <Text>Defence {formatDay(progress.defenceDate)}</Text> : null}
          </HStack>
        }
        actions={
          <HStack gap={2} flexWrap="wrap">
            <Badge variant="outline">
              Year {progress.currentYear}
            </Badge>
            <Badge variant="outline">
              {done}/{totals.length} complete
            </Badge>
          </HStack>
        }
      />

      {/* The full width, with the co-supervisor beside the years on a wide
          screen: capped at a reading width, half the screen stayed empty.
          Above the years on a narrow one, as before. */}
      <Flex
        direction={{ base: "column", xl: "row" }}
        align={{ xl: "flex-start" }}
        gap={6}
        px={{ base: 6, sm: 8, lg: 10 }}
        py={{ base: 6, lg: 8 }}
      >
        <Box w={{ base: "full", xl: "22rem" }} flexShrink={0} order={{ base: 0, xl: 1 }}>
          <CoSupervisorCard studentPersonId={progress.personId} />
        </Box>

        <Stack gap={6} flex="1" minW={0}>
          {/* The years on a card of their own, as every other list is:
              straight on the page's grey they read as loose text. */}
          <Card.Root borderWidth="1px" borderColor="border">
            <Accordion.Root multiple defaultValue={openYears} variant="plain">
              {progress.years.map((year, index) => {
                const yearDone = year.rows.filter((row) => row.status === "completed").length;
                const yearLate = year.rows.filter((row) => row.overdue).length;
                return (
                  <Accordion.Item
                    key={year.year}
                    value={String(year.year)}
                    px={5}
                    borderTopWidth={index === 0 ? 0 : "1px"}
                    borderColor="border"
                  >
                    <Accordion.ItemTrigger py={3} cursor="pointer">
                      <HStack gap={3} flex="1" flexWrap="wrap">
                        <Heading size="md" fontWeight={year.isCurrent ? "semibold" : "normal"}>
                          Year {year.year}
                        </Heading>
                        {year.isCurrent ? (
                          <Badge colorPalette="blue" variant="subtle">Now</Badge>
                        ) : null}
                        <Text fontSize="xs" color="fg.muted" fontVariantNumeric="tabular-nums">
                          {formatDay(year.startDay)} – {formatDay(year.endDay)}
                        </Text>
                        <Box flex="1" />
                        <Text fontSize="xs" color="fg.muted" fontVariantNumeric="tabular-nums">
                          {yearDone}/{year.rows.length}
                        </Text>
                        {yearLate > 0 ? (
                          <Badge
                            colorPalette="orange"
                            variant="subtle"
                            cursor="default"
                            onMouseEnter={() => setHovered({ year: year.year, what: "late" })}
                            onMouseLeave={() => setHovered(null)}
                          >
                            {yearLate} late
                          </Badge>
                        ) : null}
                      </HStack>
                      <Accordion.ItemIndicator />
                    </Accordion.ItemTrigger>
                    <Accordion.ItemContent>
                      <Accordion.ItemBody pb={4}>
                        <ProgressRows
                          rows={year.rows}
                          origin={origin}
                          highlight={hovered?.year === year.year ? hovered.what : null}
                        />
                      </Accordion.ItemBody>
                    </Accordion.ItemContent>
                  </Accordion.Item>
                );
              })}
            </Accordion.Root>
          </Card.Root>

          {/* Undated work is still owed, so it is shown rather than dropped */}
          {progress.undated.length > 0 ? (
            <Card.Root borderWidth="1px" borderColor="border">
              <Card.Body gap={2} px={5} py={4}>
                <Heading size="md" fontWeight="normal">No date yet</Heading>
                <Text fontSize="xs" color="fg.muted">
                  These have no date yet. They still need to be done.
                </Text>
                <ProgressRows rows={progress.undated} origin={origin} />
              </Card.Body>
            </Card.Root>
          ) : null}
        </Stack>
      </Flex>
    </Box>
  );
}
